import React from 'react';
import { TextField, InputAdornment } from '@mui/material';
import { FaSearch } from 'react-icons/fa';
import ProjectSearch from '../../common/ProjectSearch';
import ClusterSelector from '../../common/ClusterSelector';
import PositionSelector from '../../common/PositionSelector';
import { OrganizationalStatus, Grades, EmployeeReportingScope } from '../../common/filters';
import { PROJECT_RETURN_TYPES, CLUSTER_RETURN_TYPES, POSITION_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { EMPLOYEE_REPORTING_SCOPE } from '../../../constants/employeeReportingScope';
import { REGION_STRUCTURE_TYPE_CODE } from '../../../constants/orgStructureTypes';

const EmployeeRepositoryFilters = ({
  searchInputValue = '',
  setSearchInputValue,
  selectedPositions = [],
  setSelectedPositions,
  selectedGrades = [],
  setSelectedGrades,
  selectedProjects = [],
  setSelectedProjects,
  selectedStatuses = [],
  setSelectedStatuses,
  selectedCluster = '',
  setSelectedCluster,
  reportingScope,
  setReportingScope,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
      <div style={{ display: 'flex', gap: '16px', width: '100%', alignItems: 'flex-end' }}>
        <div style={{ width: '30%' }}>
          <TextField
            placeholder="Search individuals..."
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                )
              }
            }}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                height: '40px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2563eb',
                }
              },
              '& .MuiInputBase-input': {
                padding: '8px 14px',
              }
            }}
          />
        </div>

        <div style={{ width: '70%', display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <ProjectSearch
              multiple={true}
              value={selectedProjects}
              onChange={setSelectedProjects}
              placeholder="Search and select projects..."
              width={400}
              size="small"
              showAssignmentIndicators={false}
              returnValue={PROJECT_RETURN_TYPES.ID}
              allowNoProjects={true}
              sx={{ 
                width: '100%',
                '& .MuiFormControl-root': { width: '100%' },
                '& .MuiAutocomplete-root': { width: '100%' }
              }}
            />
          </div>

          <div style={{ flex: 1 }}>
            {/* ADR-119 amendment 1: individuals are hired in a Region — only
                Clusters of the Region structure type are offered. */}
            <ClusterSelector
              value={selectedCluster}
              onChange={setSelectedCluster}
              label="Regions"
              allowSelectAll={true}
              allOptionLabel="All Regions"
              allOptionValue=""
              structureTypeCodes={[REGION_STRUCTURE_TYPE_CODE]}
              returnValue={CLUSTER_RETURN_TYPES.ID}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: '40px',
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#E2E8F0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CBD5E1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563EB',
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <OrganizationalStatus
            selectedStatuses={selectedStatuses}
            onStatusToggle={(status) => {
              const newSelectedStatuses = selectedStatuses.includes(status)
                ? selectedStatuses.filter(s => s !== status)
                : [...selectedStatuses, status];
              setSelectedStatuses(newSelectedStatuses);
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <PositionSelector
            multiple={true}
            value={selectedPositions}
            onChange={setSelectedPositions}
            label="Positions"
            returnValue={POSITION_RETURN_TYPES.ID}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '40px',
                backgroundColor: 'white',
                '& fieldset': {
                  borderColor: '#E2E8F0',
                },
                '&:hover fieldset': {
                  borderColor: '#CBD5E1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#2563EB',
                }
              }
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Grades
            selectedGrades={selectedGrades}
            onGradeToggle={(gradeId) => {
              const newSelectedGrades = selectedGrades.includes(gradeId)
                ? selectedGrades.filter(id => id !== gradeId)
                : [...selectedGrades, gradeId];
              setSelectedGrades(newSelectedGrades);
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
        <EmployeeReportingScope
          selectedScope={reportingScope}
          onScopeChange={setReportingScope}
          disabledScopes={[]}
        />
      </div>
    </div>
  );
};

export default EmployeeRepositoryFilters;